import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Div, ThemeProvider, DefaultTheme } from '@pinpoint/design-kit';

import { isValidNumber, arrayInit, handleActuarialUpdate, handleClassifierUpdate } from 'src/functions';
import InputTable from 'src/infographics/roi_calculator_v2/inputTable';
import Footer from 'src/infographics/roi_calculator_v2/footer';
import Header from 'src/infographics/roi_calculator_v2/header';
import OutputGrid from 'src/infographics/roi_calculator_v2/outputGrid';

/* eslint-disable react/prop-types */

const version = '2.0.1';

const initialOutput = {
	y1: {
		baselinePremiums: arrayInit(10, 0),
		baselinePremiumsSum: 0,
		baselineRiskCosts: arrayInit(10, 0),
		baselineRiskCostsSum: 0,
		baselineLessRiskCost: 0,
		oneYearBaselinePifs: arrayInit(10, 0),
		oneYearBaselinePifsSum: 0,
		pinpointLift: arrayInit(10, 0),
		pinpointPifs: arrayInit(10, 0),
		pinpointPifsSum: 0,
		pinpointPremiums: arrayInit(10, 0),
		pinpointPremiumsSum: 0,
		pinpointRiskCosts: arrayInit(10, 0),
		pinpointRiskCostsSum: 0,
		pinpointLessRiskCost: 0,
		costSavings: 0,
		renewalStrategy: 0,
		targetedGrowthStrategy: 0,
	},
	y2: {
		baselinePremiums: arrayInit(10, 0),
		baselinePremiumsSum: 0,
		baselineRiskCosts: arrayInit(10, 0),
		baselineRiskCostsSum: 0,
		baselineLessRiskCost: 0,
		oneYearBaselinePifs: arrayInit(10, 0),
		oneYearBaselinePifsSum: 0,
		pinpointLift: arrayInit(10, 0),
		pinpointPifs: arrayInit(10, 0),
		pinpointPifsSum: 0,
		pinpointPremiums: arrayInit(10, 0),
		pinpointPremiumsSum: 0,
		pinpointRiskCosts: arrayInit(10, 0),
		pinpointRiskCostsSum: 0,
		pinpointLessRiskCost: 0,
		costSavings: 0,
		renewalStrategy: 0,
		targetedGrowthStrategy: 0,
	},
	y3: {
		baselinePremiums: arrayInit(10, 0),
		baselinePremiumsSum: 0,
		baselineRiskCosts: arrayInit(10, 0),
		baselineRiskCostsSum: 0,
		baselineLessRiskCost: 0,
		oneYearBaselinePifs: arrayInit(10, 0),
		oneYearBaselinePifsSum: 0,
		pinpointLift: arrayInit(10, 0),
		pinpointPifs: arrayInit(10, 0),
		pinpointPifsSum: 0,
		pinpointPremiums: arrayInit(10, 0),
		pinpointPremiumsSum: 0,
		pinpointRiskCosts: arrayInit(10, 0),
		pinpointRiskCostsSum: 0,
		pinpointLessRiskCost: 0,
		costSavings: 0,
		renewalStrategy: 0,
		targetedGrowthStrategy: 0,
	},
	cumulative: {
		y1Impact: 0,
		y2Impact: 0,
		y3Impact: 0,
	},
};

const customTheme = {
	...DefaultTheme,
	fontFamily: {
		primary: 'Inter',
		secondary: 'Inter',
		code: 'monospace',
	},
};

/* eslint-disable max-len, no-console */

function RoiCalculatorV2({
	acquisitionDistribution,
	acquisitionRate,
	averagePremium,
	baselineLift,
	baselineLosses,
	baselinePIFPercentile,
	baselinePremiums,
	carrierNonRenewalRate,
	disableInput,
	enableDiagnostics,
	minH,
	minW,
	modelType,
	onChange,
	onSave,
	pinpointLift,
	pinpointLosses,
	pinpointPIFPercentile,
	pinpointPremiums,
	policiesInForce,
	resetAcquisitionDistribution,
	riskCost,
	riskModel,
	riskModelDetail,
	yearlyCancellationRate,
	setReadyExport,
	corporateLayout,
}) {
	const [output, setOutput] = useState(initialOutput);
	const log = (...rest) => enableDiagnostics && console.log(...rest);
	const inputTableRefs = useRef(Array(6).fill(null));
	const outputTableRefs = useRef(Array(10).fill(null));
	const isInputValid = (values) => Object.values(values).every(isValidNumber);
	const acquisitionDistributionFrac = useMemo(
		() => acquisitionDistribution.map((perc) => perc / 100),
		[acquisitionDistribution]
	);

	const handleCalculate = useCallback(() => {
		const nextUserInput = {
			acquisitionRate,
			averagePremium,
			carrierNonRenewalRate,
			policiesInForce,
			riskCost,
			yearlyCancellationRate,
		};
		log('User input has changed', nextUserInput);
		if (!isInputValid(nextUserInput)) {
			log('Not all input is valid. Resetting output', initialOutput);
			setOutput(initialOutput);
			return;
		}
		if (modelType === 'CLASSIFIER') {
			const newOutput = {
				y1: null,
				y2: null,
				y3: null,
				cumulative: {},
			};
			newOutput.y1 = handleClassifierUpdate({ ...nextUserInput, acquisitionDistributionFrac, pinpointLift });
			setOutput(newOutput);
			log('Updating output', newOutput);
		}
		if (modelType.includes('ACT_')) {
			const newOutput = {
				y1: null,
				y2: null,
				y3: null,
			};
			Object.keys(newOutput).forEach((year) => {
				const currentYearInput = {
					...nextUserInput,
					acquisitionDistributionFrac,
					clientMeanLosses: baselineLosses,
					clientMeanPremiums: baselinePremiums,
					clientPIFMeanFracs: baselinePIFPercentile,
					pptMeanLosses: pinpointLosses,
					pptMeanPremiums: pinpointPremiums,
					pptPIFMeanFracs: pinpointPIFPercentile,
				};
				if (year === 'y2') {
					currentYearInput.policiesInForce = newOutput.y1.oneYearBaselinePifsSum;
					currentYearInput.lastYearClientPIF = newOutput.y1.oneYearBaselinePifs;
					currentYearInput.lastYearClientRiskCosts = newOutput.y1.baselineRiskCosts;
					currentYearInput.lastYearClientPremiums = newOutput.y1.baselinePremiums;
					currentYearInput.lastYearPptPIF = newOutput.y1.pinpointPifs;
					currentYearInput.lastYearPptRiskCosts = newOutput.y1.pinpointRiskCosts;
					currentYearInput.lastYearPptPremiums = newOutput.y1.pinpointPremiums;
				}
				if (year === 'y3') {
					currentYearInput.policiesInForce = newOutput.y2.oneYearBaselinePifsSum;
					currentYearInput.lastYearClientPIF = newOutput.y2.oneYearBaselinePifs;
					currentYearInput.lastYearClientRiskCosts = newOutput.y2.baselineRiskCosts;
					currentYearInput.lastYearClientPremiums = newOutput.y2.baselinePremiums;
					currentYearInput.lastYearPptPIF = newOutput.y2.pinpointPifs;
					currentYearInput.lastYearPptRiskCosts = newOutput.y2.pinpointRiskCosts;
					currentYearInput.lastYearPptPremiums = newOutput.y2.pinpointPremiums;
				}
				/** @type {ActuarialCalculatorOutput} */
				newOutput[year] = handleActuarialUpdate(currentYearInput);
			});
			newOutput.cumulative = {};
			newOutput.cumulative.y1Impact = newOutput.y1.costSavings;
			newOutput.cumulative.y2Impact = newOutput.y1.costSavings + newOutput.y2.costSavings;
			newOutput.cumulative.y3Impact = newOutput.y1.costSavings + newOutput.y2.costSavings + newOutput.y3.costSavings;
			newOutput.cumulative.y1NewBusinessImpact = newOutput.y1.newBusinessRoI;
			newOutput.y1.newBusinessImpact = newOutput.y1.newBusinessRoI;
			newOutput.y2.newBusinessImpact = newOutput.y1.newBusinessRoI + newOutput.y2.newBusinessRoI;
			newOutput.y3.newBusinessImpact =
				newOutput.y1.newBusinessRoI + newOutput.y2.newBusinessRoI + newOutput.y3.newBusinessRoI;
			newOutput.y1.cancellationImpact = newOutput.y1.cancellationRoI;
			newOutput.y2.cancellationImpact = newOutput.y1.cancellationRoI + newOutput.y2.cancellationRoI;
			newOutput.y3.cancellationImpact =
				newOutput.y1.cancellationRoI + newOutput.y2.cancellationRoI + newOutput.y3.cancellationRoI;
			newOutput.y1.nonRenewalImpact = newOutput.y1.nonRenewalRoI;
			newOutput.y2.nonRenewalImpact = newOutput.y1.nonRenewalRoI + newOutput.y2.nonRenewalRoI;
			newOutput.y3.nonRenewalImpact =
				newOutput.y1.nonRenewalRoI + newOutput.y2.nonRenewalRoI + newOutput.y3.nonRenewalRoI;
			newOutput.cumulative.newBusinessImpact =
				3 * newOutput.y1.newBusinessRoI + 2 * newOutput.y2.newBusinessRoI + newOutput.y3.newBusinessRoI;
			newOutput.cumulative.nonRenewalImpact =
				3 * newOutput.y1.nonRenewalRoI + 2 * newOutput.y2.nonRenewalRoI + newOutput.y3.nonRenewalRoI;
			newOutput.cumulative.cancellationImpact =
				3 * newOutput.y1.cancellationRoI + 2 * newOutput.y2.cancellationRoI + newOutput.y3.cancellationRoI;

			setOutput(newOutput);
			log('Updating output', newOutput);
		}
		setReadyExport(true);
	}, [
		pinpointLift,
		acquisitionRate,
		averagePremium,
		carrierNonRenewalRate,
		policiesInForce,
		riskCost,
		yearlyCancellationRate,
		baselinePremiums,
		baselinePIFPercentile,
		pinpointPremiums,
		pinpointPIFPercentile,
		acquisitionDistribution,
		acquisitionDistributionFrac,
	]);

	useEffect(() => {
		onSave({
			version,
			input: {
				acquisitionDistributionFrac,
				acquisitionRate,
				averagePremium,
				carrierNonRenewalRate,
				policiesInForce,
				riskCost,
				yearlyCancellationRate,
			},
			output,
		});
	}, [output]);

	if (!riskModel || !pinpointLift) return <Div>Error</Div>;
	return (
		<Div
			h="100%"
			w="100%"
			minH={minH}
			minW={minW}
			m="0"
			p={{ x: '1.25rem', y: '1rem' }}
			style={{ overflowX: 'hidden', overflowY: 'scroll' }}
		>
			<Div
				d="flex"
				justify="space-between"
				align="stretch"
				m="0"
				style={{ gap: '.5rem' }}
				data-test="header-row"
			>
				<Header type={modelType} corporateLayout={corporateLayout} />
				<InputTable
		      corporateLayout={corporateLayout}
					acquisitionRate={acquisitionRate}
					averagePremium={averagePremium}
					carrierNonRenewalRate={carrierNonRenewalRate}
					disableInput={disableInput}
					inputRefs={inputTableRefs.current}
					modelType={modelType}
					onChange={onChange}
					output={output}
					policiesInForce={policiesInForce}
					riskCost={riskCost}
					riskModel={riskModel}
					riskModelDetail={riskModelDetail}
					yearlyCancellationRate={yearlyCancellationRate}
				/>
			</Div>
			<Div m={{ t: '.75rem' }} d="flex" w="100%">
				<OutputGrid
					acquisitionDistribution={acquisitionDistribution}
					acquisitionRate={acquisitionRate}
					averagePremium={averagePremium}
					baselineLift={baselineLift}
					carrierNonRenewalRate={carrierNonRenewalRate}
					disableInput={disableInput}
					handleCalculate={handleCalculate}
					inputRefs={outputTableRefs.current}
					modelType={modelType}
					onChange={onChange}
					output={output}
					pinpointLift={pinpointLift}
					policiesInForce={policiesInForce}
					resetAcquisitionDistribution={resetAcquisitionDistribution}
					riskCost={riskCost}
					yearlyCancellationRate={yearlyCancellationRate}
				/>
			</Div>
			<Footer />
		</Div>
	);
}

RoiCalculatorV2.defaultProps = {
	minH: '100%',
	minW: '100%',
	riskModel: '',
	pinpointLift: [],
	enableDiagnostics: false,
	onChange: () => {},
	onSave: () => {},
	// NOTE: Initial user input
	acquisitionRate: '',
	averagePremium: '',
	carrierNonRenewalRate: '',
	hasBaselineModel: false,
	policiesInForce: '',
	riskCost: '',
	yearlyCancellationRate: '',
	modelType: 'CLASSIFIER',
};

function ThemedRoICalculatorV2(props) {
	return <ThemeProvider theme={customTheme}>{RoiCalculatorV2(props)}</ThemeProvider>;
}

export default ThemedRoICalculatorV2;
